// "Same prompt, every model" (V4-MS): the model showcase as a landing section. One style at a time (chips), every
// video model's clip of it in a row you can swipe; tap one to play it with sound. Renders only once the showcase has
// clips (GET /api/model-showcase); until then, or if the request fails, the page simply doesn't have this section.
import { useEffect, useState } from "react";
import { cellOf, hasShowcase, loadModelShowcase, pickStyle, speedOf, type ShowcaseData } from "../showcase";
import { IconPlay } from "./parts";
import "./model-showcase.css";

export function ModelShowcase({ live = true, initial = null }: { live?: boolean; initial?: ShowcaseData | null }) {
  const [data, setData] = useState<ShowcaseData | null>(initial);
  const [styleId, setStyleId] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  useEffect(() => {
    if (!live) return;
    let on = true;
    void loadModelShowcase().then((d) => on && d && setData(d));
    return () => {
      on = false;
    };
  }, [live]);
  if (!hasShowcase(data)) return null;
  const style = pickStyle(data, styleId)!;
  const models = data.models.filter((m) => cellOf(data, style.id, m.id));

  return (
    <section id="models" className="lp-section lp-models" aria-labelledby="lp-models">
      <p className="lp-eyebrow">Same prompt, every model</p>
      <h2 id="lp-models" className="lp-h2">
        See the difference first.
      </h2>
      <p className="lp-lede">
        We gave every video model in the studio the same picture and the same direction. {models.length} models, {data.styles.length} styles, the first take from each. Your Director helps you pick the right one for each scene.
      </p>
      <div className="lp-models-styles" role="tablist" aria-label="Styles">
        {data.styles.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === style.id}
            className={`lp-models-chip${s.id === style.id ? " is-on" : ""}`}
            onClick={() => {
              setStyleId(s.id);
              setPlaying(null);
            }}
          >
            {s.name}
          </button>
        ))}
      </div>
      <p className="lp-models-watch">{style.watch}</p>
      <ul className="lp-models-row" aria-label={`${style.name} on every model`}>
        {models.map((m) => {
          const c = cellOf(data, style.id, m.id)!;
          const key = `${style.id}|${m.id}`;
          return (
            <li key={m.id} className="lp-models-item">
              {playing === key ? (
                <video className="lp-models-clip" src={c.videoUrl} poster={c.posterUrl || undefined} controls autoPlay playsInline onEnded={() => setPlaying(null)} />
              ) : (
                <button type="button" className="lp-models-clip" onClick={() => setPlaying(key)} aria-label={`Play ${m.short}: ${style.name}`}>
                  {c.posterUrl && <img src={c.posterUrl} alt="" loading="lazy" decoding="async" />}
                  <span className="lp-models-play" aria-hidden>
                    <IconPlay />
                  </span>
                </button>
              )}
              <p className="lp-models-cap">
                <b>{m.short}</b>
                <span>{[speedOf(m) === "fast" ? "Fast" : null, m.hasAudio ? "With sound" : null, `${m.creditsPer5s} credits / 5 s`].filter(Boolean).join(" · ")}</span>
              </p>
            </li>
          );
        })}
      </ul>
      <p className="lp-models-note">{data.note}</p>
    </section>
  );
}
